import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeftIcon, UserIcon, XIcon } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { type ReactNode } from "react";
import { type PublicSessionInfo } from "../../hooks/use-public-session-info";
import { inIframe, minimizeEmbed } from "../../lib/embed";
import { AgentAvatar } from "@/components/molecules/agent-avatar";
import { usePublicTranslation } from "@/features/utils/i18n";

interface ChatHeaderProps {
  sessionInfo?: PublicSessionInfo;
  onInfoClick?: () => void;
  /**
   * Open the list of the visitor's past conversations. Starting a new one lives there
   * and only there — it is a decision to leave the thread, so it belongs next to the
   * threads it would leave, not one slip of the thumb away from the message being typed.
   *
   * It is a **back arrow, on the left**, because that is what it does: the list is
   * where this conversation came from. As an icon on the right among the others it
   * read as a fourth unrelated action.
   */
  onConversationsClick?: () => void;
}

export function ChatHeader({
  sessionInfo,
  onInfoClick,
  onConversationsClick,
}: ChatHeaderProps) {
  const { t } = usePublicTranslation();
  if (!sessionInfo) {
    return (
      <div className="border-b bg-background/80 p-4 backdrop-blur">
        <div className="flex items-center gap-3">
          <Skeleton className="size-10 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
      </div>
    );
  }

  // The chatbox is personal: lead with the agent's name ("Parler avec Léo"),
  // with the company as the supporting line. Fall back to the company name when
  // no agent is set.
  const agentName = sessionInfo.agent?.name;
  const title = agentName || sessionInfo.client.name;
  const subtitle = agentName ? sessionInfo.client.name : undefined;

  return (
    <div className="flex items-stretch border-b bg-background/80 backdrop-blur">
      {onConversationsClick && (
        <div className="flex shrink-0 items-center pl-1">
          <HeaderAction label={t("chat.conversations")} onClick={onConversationsClick}>
            <ArrowLeftIcon className="size-5" />
          </HeaderAction>
        </div>
      )}
      <button
        onClick={onInfoClick}
        className={cn(
          "flex min-w-0 flex-1 items-center gap-3 py-4 pr-2 text-left transition-colors hover:bg-muted/40",
          onConversationsClick ? "pl-1" : "pl-4",
        )}
      >
        <AgentAvatar
          name={title}
          logo={sessionInfo.branding?.logo || sessionInfo.client.logo}
          className="size-10"
          online
        />
        <div className="min-w-0 flex-1">
          <h1 className="flex items-center gap-2 truncate text-base font-semibold">
            {title}
          </h1>
          <p className="flex items-center gap-1.5 truncate text-xs text-muted-foreground">
            <span className="inline-block size-1.5 rounded-full bg-success" />
            {t("chat.online")}
            {subtitle && (
              <>
                <span className="opacity-40">·</span>
                <span className="truncate">{subtitle}</span>
              </>
            )}
          </p>
        </div>
      </button>

      {/* One group, one gap: the info icon used to sit inside the title button, so the
          space before it was that button's padding and the icons read as unevenly spread. */}
      <div className="flex shrink-0 items-center gap-0.5 py-2 pr-2">
        {/* What this panel holds is the visitor's own details — their contacts, their
            appointments, signing in. An "i" promised a page about the business. */}
        {onInfoClick && (
          <HeaderAction label={t("chat.account")} onClick={onInfoClick}>
            <UserIcon className="size-5" weight="regular" />
          </HeaderAction>
        )}
        <CloseAction />
      </div>
    </div>
  );
}

/**
 * Put the widget away — only meaningful inside the embed iframe, where there is a
 * launcher to go back to.
 *
 * A cross, not the two facing carets it used to be: those read as "collapse" and
 * nobody knew what they would collapse into. The conversation is not lost by pressing
 * it, which is what the launcher coming back says.
 */
export function CloseAction() {
  const { t } = usePublicTranslation();
  if (!inIframe()) return null;
  return (
    <HeaderAction label={t("chat.close")} onClick={minimizeEmbed}>
      <XIcon className="size-5" weight="bold" />
    </HeaderAction>
  );
}

/** Icon button of the chat header — same target size and hover as the others. */
export function HeaderAction({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className="flex size-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
    >
      {children}
    </button>
  );
}
